import { HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, distinctUntilChanged, map, of, startWith, switchMap } from 'rxjs';
import { ProductionEfficiencyMatrixComponent } from 'src/app/components/main/employees/production/production-efficiency-matrix/production-efficiency-matrix.component';
import { KpiEvaluationPreviewDialogComponent } from 'src/app/components/main/employees/performance/my-metrics/kpi-evaluation-preview-dialog/kpi-evaluation-preview-dialog.component';
import {
  MonthNavigationComponent,
  formatMonthKeyFromDate,
  formatMonthPeriodLabel,
  getNextMonthKey,
  getPreviousMonthKey,
  normalizeMonthKey,
  shiftMonthKey,
} from 'src/app/components/main/shared/month-navigation/month-navigation.component';
import { ProductionKeyPerformanceIndicatorEvaluationEnum } from 'src/app/enums/production-key-performance-indicator-evaluation.enum';
import {
  ProductionKpiAxisDisplay,
  ProductionKpiMatrixCell,
  ProductionKpiMatrixCellMember,
  ProductionKpiMatrixColumn,
  ProductionKpiMatrixMode,
  ProductionKpiMatrixResponse,
  ProductionKpiMatrixRow,
} from 'src/app/models/production-kpi-matrix.model';
import { getKpiEvaluationStyle, KPI_EVALUATION_STYLES } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { ApiService } from 'src/app/services/api.service';

const DEFAULT_PROFILE_PICTURE = 'assets/img/default_profile_picture.png';
const DEFAULT_COMPANY_PRODUCT_LOGO = '/img/default-company_product.png';

@Component({
  selector: 'app-production-kpi-matrix',
  templateUrl: './production-kpi-matrix.component.html',
  styleUrl: './production-kpi-matrix.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatButtonToggleModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MonthNavigationComponent,
    ProductionEfficiencyMatrixComponent,
  ],
})
export class ProductionKpiMatrixComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly activated_route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);

  private readonly current_month_key = formatMonthKeyFromDate(new Date());
  private readonly query_params = toSignal(this.activated_route.queryParamMap, {
    initialValue: this.activated_route.snapshot.queryParamMap,
  });

  readonly evaluation_options = [
    ProductionKeyPerformanceIndicatorEvaluationEnum.Good,
    ProductionKeyPerformanceIndicatorEvaluationEnum.Medium,
    ProductionKeyPerformanceIndicatorEvaluationEnum.Poor,
  ] as const;

  readonly evaluation_styles = KPI_EVALUATION_STYLES;
  private readonly default_evaluations: readonly ProductionKeyPerformanceIndicatorEvaluationEnum[] = [
    ProductionKeyPerformanceIndicatorEvaluationEnum.Medium,
    ProductionKeyPerformanceIndicatorEvaluationEnum.Poor,
  ];

  private readonly collapsed_company_product_ids = signal<ReadonlySet<string>>(new Set());

  readonly selected_month = computed((): string => this.resolveMonth(this.query_params().get('month')));
  readonly selected_period_end = computed((): string => this.resolvePeriodEnd(this.query_params().get('period_end')));
  readonly efficiency_from_month = computed((): string => shiftMonthKey(this.selected_period_end(), -2));
  readonly efficiency_to_month = computed((): string => this.selected_period_end());
  readonly efficiency_period_label = computed((): string =>
    formatMonthPeriodLabel(this.efficiency_from_month(), this.efficiency_to_month())
  );
  readonly selected_mode = computed((): ProductionKpiMatrixMode => this.resolveMode(this.query_params().get('mode')));
  readonly selected_axis_display = computed(
    (): ProductionKpiAxisDisplay => this.resolveAxisDisplay(this.query_params().get('mode'), this.query_params().get('axis_display'))
  );
  readonly selected_evaluations = computed((): ProductionKeyPerformanceIndicatorEvaluationEnum[] =>
    this.resolveEvaluations(this.query_params().get('evaluation'))
  );

  readonly can_go_to_next_month = computed((): boolean => {
    if (this.is_efficiency_mode()) {
      return this.selected_period_end() < this.current_month_key;
    }

    return this.selected_month() < this.current_month_key;
  });
  readonly can_go_to_current_month = computed((): boolean => {
    if (this.is_efficiency_mode()) {
      return this.selected_period_end() !== this.current_month_key;
    }

    return this.selected_month() !== this.current_month_key;
  });
  readonly is_efficiency_mode = computed((): boolean => this.selected_mode() === 'efficiency');
  readonly month_navigation_selected = computed((): string =>
    this.is_efficiency_mode() ? this.selected_period_end() : this.selected_month()
  );
  readonly month_navigation_display_label = computed((): string | null =>
    this.is_efficiency_mode() ? this.efficiency_period_label() : null
  );
  readonly month_navigation_current_tooltip = computed((): string =>
    this.is_efficiency_mode() ? 'Ir al periodo actual (últimos 3 meses, incluido el actual)' : 'Ir al mes actual'
  );

  private readonly matrix_http_params = computed((): string | null => {
    if (this.is_efficiency_mode()) {
      return null;
    }

    let params = new HttpParams().set('month', this.selected_month()).set('mode', this.selected_mode());
    const evaluations = this.selected_evaluations();
    if (evaluations.length > 0) {
      params = params.set('evaluation', evaluations.join(','));
    }
    return params.toString();
  });

  private readonly matrix_request = toSignal(
    toObservable(this.matrix_http_params).pipe(
      distinctUntilChanged(),
      switchMap(params_string => {
        if (params_string === null) {
          return of({
            status: 'ready' as const,
            matrix: null as ProductionKpiMatrixResponse | null,
            error: null as string | null,
          });
        }

        return this.api.getProductionKpiMatrix(new HttpParams({ fromString: params_string })).pipe(
          map(matrix => ({ status: 'ready' as const, matrix, error: null as string | null })),
          catchError(() =>
            of({
              status: 'error' as const,
              matrix: null as ProductionKpiMatrixResponse | null,
              error: 'No se ha podido cargar la matriz de métricas.',
            })
          ),
          startWith({
            status: 'loading' as const,
            matrix: null as ProductionKpiMatrixResponse | null,
            error: null as string | null,
          })
        );
      })
    ),
    {
      initialValue: {
        status: 'loading' as const,
        matrix: null as ProductionKpiMatrixResponse | null,
        error: null as string | null,
      },
    }
  );

  readonly is_loading = computed((): boolean => this.matrix_request().status === 'loading');
  readonly error_message = computed((): string | null => this.matrix_request().error);

  readonly view_model = computed((): ProductionKpiMatrixViewModel | null => {
    const matrix = this.matrix_request().matrix;
    if (matrix === null) {
      return null;
    }

    const axis_display = this.selected_axis_display();
    const mode = this.selected_mode();
    const show_column_name = mode === 'users' ? axis_display === 'name_and_photo' : axis_display === 'logo_and_name';
    const collapsed = this.collapsed_company_product_ids();

    const columns = matrix.columns.map(
      (column: ProductionKpiMatrixColumn): ProductionKpiMatrixColumnView => ({
        id: column.id,
        name: column.name,
        photo_url: this.resolveColumnPhotoUrl(column, mode, axis_display),
        show_name: show_column_name,
        name_tooltip: show_column_name ? null : column.name,
        general_color: this.evaluationToColor(matrix.column_general_evaluations[String(column.id)] ?? null),
      })
    );

    const rows = matrix.rows
      .filter((row: ProductionKpiMatrixRow): boolean => row.parent_row_id === null || !collapsed.has(row.parent_row_id))
      .map((row: ProductionKpiMatrixRow): ProductionKpiMatrixRowView => {
        const cells = columns.map((column: ProductionKpiMatrixColumnView): ProductionKpiMatrixCellView => {
          const cell = row.cells[String(column.id)] ?? null;
          return this.serializeCell(cell, row);
        });

        return {
          id: row.id,
          kind: row.kind,
          label: row.label,
          description: row.kind === 'kpi' && row.description ? row.description : null,
          company_product_logo_url: row.company_product_logo_url || DEFAULT_COMPANY_PRODUCT_LOGO,
          show_company_product_logo: row.kind === 'company_product_header',
          collapsible: row.collapsible,
          is_collapsed: collapsed.has(row.id),
          is_child: row.parent_row_id !== null,
          cell_layout: row.cell_layout ?? 'single',
          row_general_color: this.evaluationToColor(row.row_general_evaluation),
          cells,
        };
      });

    return {
      global_general_color: this.evaluationToColor(matrix.global_general_evaluation),
      columns,
      rows,
    };
  });

  setMode(event: MatButtonToggleChange): void {
    const mode = this.resolveMode(String(event.value));
    this.navigateFilters({
      mode,
      axis_display: mode === 'efficiency' ? null : this.resolveAxisDisplay(mode, null),
    });
  }

  setAxisDisplay(event: MatButtonToggleChange): void {
    this.navigateFilters({
      axis_display: this.resolveAxisDisplay(this.selected_mode(), String(event.value)),
    });
  }

  goToPreviousMonth(): void {
    if (this.is_efficiency_mode()) {
      this.navigateFilters({ period_end: getPreviousMonthKey(this.selected_period_end()) });
      return;
    }

    this.navigateFilters({ month: getPreviousMonthKey(this.selected_month()) });
  }

  goToNextMonth(): void {
    if (!this.can_go_to_next_month()) {
      return;
    }

    if (this.is_efficiency_mode()) {
      this.navigateFilters({ period_end: getNextMonthKey(this.selected_period_end()) });
      return;
    }

    this.navigateFilters({ month: getNextMonthKey(this.selected_month()) });
  }

  goToCurrentMonth(): void {
    if (this.is_efficiency_mode()) {
      this.navigateFilters({ period_end: this.current_month_key });
      return;
    }

    this.navigateFilters({ month: this.current_month_key });
  }

  setEvaluations(event: MatButtonToggleChange): void {
    const raw_values = Array.isArray(event.value) ? event.value : [];
    const evaluations = raw_values
      .map((value: unknown): ProductionKeyPerformanceIndicatorEvaluationEnum | null => {
        if (
          value === ProductionKeyPerformanceIndicatorEvaluationEnum.Good ||
          value === ProductionKeyPerformanceIndicatorEvaluationEnum.Medium ||
          value === ProductionKeyPerformanceIndicatorEvaluationEnum.Poor
        ) {
          return value;
        }
        return null;
      })
      .filter((value): value is ProductionKeyPerformanceIndicatorEvaluationEnum => value !== null);

    this.navigateFilters({ evaluations });
  }

  toggleCompanyProductRow(row_id: string): void {
    this.collapsed_company_product_ids.update((current: ReadonlySet<string>): ReadonlySet<string> => {
      const next = new Set(current);
      if (next.has(row_id)) {
        next.delete(row_id);
      } else {
        next.add(row_id);
      }
      return next;
    });
  }

  openCellDetail(cell: ProductionKpiMatrixCellView): void {
    if (cell.pkpi_id == null || cell.evaluation == null) {
      return;
    }

    this.dialog.open(KpiEvaluationPreviewDialogComponent, {
      data: {
        kind: 'fetch',
        month: this.selected_month(),
        production_key_performance_indicator_id: cell.pkpi_id,
        user_id: cell.user_id,
        company_product_id: cell.company_product_id,
      },
      panelClass: 'kpi-preview-dialog-panel',
      autoFocus: false,
    });
  }

  openMemberDetail(cell: ProductionKpiMatrixCellView, member: ProductionKpiMatrixMemberView): void {
    if (cell.pkpi_id == null) {
      return;
    }

    this.dialog.open(KpiEvaluationPreviewDialogComponent, {
      data: {
        kind: 'fetch',
        month: this.selected_month(),
        production_key_performance_indicator_id: cell.pkpi_id,
        user_id: member.user_id,
        company_product_id: cell.company_product_id,
      },
      panelClass: 'kpi-preview-dialog-panel',
      autoFocus: false,
    });
  }

  private navigateFilters(partial: {
    mode?: ProductionKpiMatrixMode;
    month?: string;
    period_end?: string;
    evaluations?: ProductionKeyPerformanceIndicatorEvaluationEnum[];
    axis_display?: ProductionKpiAxisDisplay | null;
  }): void {
    const mode = partial.mode ?? this.selected_mode();
    const month = partial.month ?? this.selected_month();
    const period_end = partial.period_end ?? this.selected_period_end();
    const evaluations = mode === 'efficiency' ? this.default_evaluations : (partial.evaluations ?? this.selected_evaluations());
    const axis_display =
      mode === 'efficiency' ? null : (partial.axis_display ?? this.resolveAxisDisplay(mode, this.selected_axis_display()));

    void this.router.navigate([], {
      relativeTo: this.activated_route,
      queryParams: {
        mode: mode === 'users' ? null : mode,
        month: mode === 'efficiency' || month === this.current_month_key ? null : month,
        period_end: mode !== 'efficiency' || period_end === this.current_month_key ? null : period_end,
        evaluation: mode === 'efficiency' || this.isDefaultEvaluations([...evaluations]) ? null : evaluations.join(',') || null,
        axis_display: axis_display === null || this.isDefaultAxisDisplay(mode, axis_display) ? null : axis_display,
      },
      queryParamsHandling: 'merge',
    });
  }

  private serializeCell(cell: ProductionKpiMatrixCell | null, row: ProductionKpiMatrixRow): ProductionKpiMatrixCellView {
    if (cell === null) {
      return {
        evaluation: null,
        color: null,
        tooltip: null,
        pkpi_id: row.pkpi_id,
        user_id: null,
        company_product_id: row.company_product_id,
        clickable: false,
        members: [],
      };
    }

    const color = this.evaluationToColor(cell.evaluation);
    const members = (cell.members ?? []).map(
      (member: ProductionKpiMatrixCellMember): ProductionKpiMatrixMemberView => ({
        user_id: member.user_id,
        name: member.name,
        photo_url: member.serious_profile_picture_url || DEFAULT_PROFILE_PICTURE,
        border_color: this.evaluationToColor(member.evaluation),
        tooltip: this.buildMemberTooltip(member.name, member.tooltip ?? null),
      })
    );

    return {
      evaluation: cell.evaluation,
      color,
      tooltip: cell.tooltip ?? null,
      pkpi_id: cell.pkpi_id,
      user_id: cell.user_id,
      company_product_id: cell.company_product_id,
      clickable: row.kind === 'kpi' && row.cell_layout !== 'members_grid' && cell.evaluation != null && cell.pkpi_id != null,
      members,
    };
  }

  private buildMemberTooltip(name: string | null, evaluation_tooltip: string | null): string | null {
    const parts = [name, evaluation_tooltip].filter((part): part is string => part != null && part !== '');
    return parts.length > 0 ? parts.join(' — ') : null;
  }

  private resolveColumnPhotoUrl(
    column: ProductionKpiMatrixColumn,
    mode: ProductionKpiMatrixMode,
    axis_display: ProductionKpiAxisDisplay
  ): string {
    if (mode === 'company_products') {
      return column.logo_url || DEFAULT_COMPANY_PRODUCT_LOGO;
    }
    if (axis_display === 'photo_formal') {
      return column.serious_profile_picture_url || DEFAULT_PROFILE_PICTURE;
    }
    return column.pokemon_profile_picture_url || DEFAULT_PROFILE_PICTURE;
  }

  private evaluationToColor(evaluation: ProductionKeyPerformanceIndicatorEvaluationEnum | null | undefined): string | null {
    if (evaluation == null) {
      return null;
    }
    return getKpiEvaluationStyle(evaluation).color;
  }

  private resolveMode(value: string | null): ProductionKpiMatrixMode {
    if (value === 'company_products' || value === 'efficiency') {
      return value;
    }
    return 'users';
  }

  private resolveMonth(value: string | null): string {
    return normalizeMonthKey(value) ?? this.current_month_key;
  }

  private resolvePeriodEnd(value: string | null): string {
    const normalized = normalizeMonthKey(value);
    if (normalized === null || normalized > this.current_month_key) {
      return this.current_month_key;
    }

    return normalized;
  }

  private resolveEvaluations(value: string | null): ProductionKeyPerformanceIndicatorEvaluationEnum[] {
    if (value === null) {
      return [...this.default_evaluations];
    }

    if (value.trim() === '') {
      return [];
    }

    return value
      .split(',')
      .map((item: string): string => item.trim())
      .filter(
        (item: string): item is ProductionKeyPerformanceIndicatorEvaluationEnum =>
          item === ProductionKeyPerformanceIndicatorEvaluationEnum.Good ||
          item === ProductionKeyPerformanceIndicatorEvaluationEnum.Medium ||
          item === ProductionKeyPerformanceIndicatorEvaluationEnum.Poor
      );
  }

  private resolveAxisDisplay(mode_value: string | null, axis_value: string | null): ProductionKpiAxisDisplay {
    const mode = this.resolveMode(mode_value);
    if (mode === 'efficiency') {
      return 'photo_formal';
    }
    if (mode === 'company_products') {
      if (axis_value === 'logo' || axis_value === 'logo_and_name') {
        return axis_value;
      }
      return 'logo';
    }
    if (axis_value === 'photo_informal' || axis_value === 'photo_formal' || axis_value === 'name_and_photo') {
      return axis_value;
    }
    return 'photo_formal';
  }

  private isDefaultAxisDisplay(mode: ProductionKpiMatrixMode, axis_display: ProductionKpiAxisDisplay): boolean {
    if (mode === 'efficiency') {
      return true;
    }
    return mode === 'users' ? axis_display === 'photo_formal' : axis_display === 'logo';
  }

  private isDefaultEvaluations(evaluations: ProductionKeyPerformanceIndicatorEvaluationEnum[]): boolean {
    if (evaluations.length !== this.default_evaluations.length) {
      return false;
    }

    return this.default_evaluations.every(evaluation => evaluations.includes(evaluation));
  }
}

interface ProductionKpiMatrixMemberView {
  user_id: number;
  name: string | null;
  photo_url: string;
  border_color: string | null;
  tooltip: string | null;
}

interface ProductionKpiMatrixCellView {
  evaluation: ProductionKeyPerformanceIndicatorEvaluationEnum | null;
  color: string | null;
  tooltip: string | null;
  pkpi_id: number | null;
  user_id: number | null;
  company_product_id: number | null;
  clickable: boolean;
  members: ProductionKpiMatrixMemberView[];
}

interface ProductionKpiMatrixColumnView {
  id: number;
  name: string;
  photo_url: string;
  show_name: boolean;
  name_tooltip: string | null;
  general_color: string | null;
}

interface ProductionKpiMatrixRowView {
  id: string;
  kind: 'kpi' | 'company_product_header';
  label: string;
  description: string | null;
  company_product_logo_url: string;
  show_company_product_logo: boolean;
  collapsible: boolean;
  is_collapsed: boolean;
  is_child: boolean;
  cell_layout: 'single' | 'members_grid';
  row_general_color: string | null;
  cells: ProductionKpiMatrixCellView[];
}

interface ProductionKpiMatrixViewModel {
  global_general_color: string | null;
  columns: ProductionKpiMatrixColumnView[];
  rows: ProductionKpiMatrixRowView[];
}
